import React, { FC, useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { IconBackground } from 'common/components';
import { EColor } from 'common/const';
import { LIST_LIMIT_0 } from 'common/config';
import { Location, Bin, Plus } from 'common/svg';
import { showMessage } from 'common/helpers';
import { WarningCard } from 'common/components/WarningCard';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { AddressCreateCard } from 'entities/Address/components';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import {
  createAddress,
  deleteAddress,
  getAddressList,
  selectAddressList,
  selectIsAddressLoading,
  setAddressList,
} from 'entities/Address/Address.slice';
import { getSetSelfRegistrationPreviousStep } from 'entities/SelfRegistration/SelfRegistration.slice';

interface IComponentProps {
  onFinish: () => void;
  loading: boolean;
  error: string | null;
}

export const SelfRegistrationAddresses: FC<IComponentProps> = ({ onFinish, loading, error }) => {
  const dispatch = useAppDispatch();
  // address
  const addressList = useAppSelector(selectAddressList);
  const isAddressLoading = useAppSelector(selectIsAddressLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openCreateCard, setOpenCreateCard] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null);

  useEffect(() => {
    const fetch = async () => {
      dispatch(setAddressList([]));
      await dispatch(getAddressList({ limit: LIST_LIMIT_0 }));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  const onCreateClick = () => {
    setOpenCreateCard(true);
  };

  const onCreateSubmit = (payload: ICreateAddressPayload) => {
    dispatch(createAddress(payload));
  };

  const onDeleteClick = (record: IAddress) => {
    setSelectedAddress(record);
    setOpenWarningCard(true);
  };

  const onDeleteConfirm = () => {
    if (selectedAddress) {
      dispatch(
        deleteAddress({
          id: selectedAddress.id,
          onSuccess: () => {
            showMessage({
              content: `Адрес «${selectedAddress.name}» удалён.`,
              icon: <Bin className="icon-bin-dark-grey" />,
            });
            setOpenWarningCard(false);
          },
        }),
      );
    }
  };

  const backward = (): void => {
    dispatch(getSetSelfRegistrationPreviousStep());
  };

  const hasAddresses = addressList.length > 0;

  return (
    <div className="self-registration-step">
      <div className="self-registration-step__title">Добавьте адреса доставки</div>

      {error && <Alert type="error" message={error} showIcon />}

      <div className="mb-6">
        <Button className="button-sm primary" icon={<Plus className="icon-plus-white" />} onClick={onCreateClick}>
          Добавить адрес
        </Button>
      </div>

      {fetchLoading ? null : !hasAddresses ? (
        <div className="self-registration-step__placeholder">Адреса доставки ещё не добавлены.</div>
      ) : (
        <div className="self-registration-step__list">
          {addressList.map((address) => (
            <div key={address.id} className="self-registration-step__list-item">
              <div className="self-registration-step__list-item-info">
                <IconBackground content={<Location className="icon-location-bright-green" />} color={EColor.PaleGreen} />

                <div>
                  <div className="text-controls-accent mb-2">{address.name}</div>
                  <span className="text-tag color-dark-grey">{address.city.name}</span>
                  {address.postalCode && <div className="text-tag color-dark-grey">{address.postalCode}</div>}
                </div>
              </div>

              <Button
                className="button-icon"
                icon={<Bin className="icon-bin-dark-grey" />}
                onClick={() => onDeleteClick(address)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="self-registration-step__actions">
        <Button className="button-lg secondary" loading={loading} onClick={backward}>
          Назад
        </Button>
        <Button className="button-lg primary" loading={loading} onClick={onFinish}>
          {hasAddresses ? 'Завершить' : 'Пропустить'}
        </Button>
      </div>

      <AddressCreateCard
        open={openCreateCard}
        loading={isAddressLoading}
        address={null}
        onCancel={() => setOpenCreateCard(false)}
        onSubmit={onCreateSubmit}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить адрес?"
        subtitle="Адрес будет удалён."
        confirmBtnTitle="Удалить"
        loading={isAddressLoading}
        onConfirm={onDeleteConfirm}
        onClose={() => setOpenWarningCard(false)}
      />
    </div>
  );
};
